/**
 * Enforcing "no network calls at runtime" (BRIEF-COMMON) as a mechanism rather than a promise.
 *
 * `GLTFLoader.parse(buffer, '')` reads the main file from memory, but a glTF asset can still
 * *name* external resources — `buffers[].uri`, `images[].uri` — and the loader will happily
 * resolve them through its `LoadingManager` and fetch them. An imported GLB is untrusted input:
 * whether it phones home must not depend on what the file happens to contain (audit P1).
 *
 * `LoadingManager.setURLModifier` runs before any request is issued, which is exactly the hook
 * for this. Anything that is not already local bytes (`data:`, `blob:`) is refused loudly, so a
 * model that needs an external resource fails with an explanation instead of silently reaching
 * for the network.
 */

import { LoadingManager } from 'three';

import { AppError } from '../shared';

/** URI schemes that are already local bytes and never cause a request to leave the machine. */
const LOCAL_SCHEMES = ['data:', 'blob:'];

/** True when `url` is a self-contained payload rather than something to go and fetch. */
export function isLocalResourceUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase();
  return LOCAL_SCHEMES.some((scheme) => trimmed.startsWith(scheme));
}

/**
 * A `LoadingManager` that refuses to resolve any URL a loader would have to fetch.
 *
 * Throwing from the URL modifier is deliberate: three.js calls it synchronously on the path that
 * would otherwise create the request, so the throw both prevents the fetch and surfaces as a
 * parse failure the user can read.
 */
export function createLocalOnlyManager(context = 'model'): LoadingManager {
  const manager = new LoadingManager();
  manager.setURLModifier((url: string) => {
    if (isLocalResourceUrl(url)) return url;
    throw new AppError(
      'EXTERNAL_RESOURCE_BLOCKED',
      `This ${context} references an external resource ("${url}"). Motion Kit never loads ` +
        'anything over the network — re-export the file with its textures and buffers embedded.',
      { url },
    );
  });
  return manager;
}
